// src/i18n/translations.ts
export type Lang = "es" | "en";
export type Translations = Record<string, any>;

export const translations: Record<Lang, Translations> = {
  en: {
    common: {
      language: "Language",
      es: "ES",
      en: "EN",
    },
    shell: {
      brandStudio: "Brand Studio",
      creatorStudio: "Creator Studio",
      searchBrand: "Search prompts, styles, kits...",
      searchCreator: "Search prompts, styles, 4k, upscale...",
      exploreNoSession: "Explore (no session)",
      previewHint: "Create an account or log in to generate",
      loginRequired: "Log in to access",
      login: "Log in",
      signup: "Sign up",
      logout: "Log out",
      collapseMenu: "Collapse menu",
      expandMenu: "Expand menu",
      creditsLoading: "credits: …",
      credits: "credits",
      plan: "Plan",
    },
    nav: {
      home: "Home",
      generator: "AI Generator",
      projects: "Projects",
      brandKit: "Brand Kit",
      settings: "Settings",
    },

    home: {
      nav: {
        creator: "Creator",
        brand: "Brand",
        howItWorks: "How it works",
        pricing: "Pricing",
        login: "Log in",
        signup: "Sign up",
        mobileLogin: "Log in",
        mobileSignup: "Sign up",
        openCreatorTitle: "Open Creator (Preview)",
        openBrandTitle: "Open Brand (Preview)",
      },
      hero: {
        titleLine1: "Create professional content with AI.",
        titleEmphasis: "Built for creators and brands.",
        subtitleLine1: "Ovylon is not just image generation.",
        subtitleLine2: "It’s a creation system that turns ideas into business-ready results.",
        ctaPrimary: "Create free account →",
        ctaSecondary: "Log in",
        note: "No card · Free credits to start",
      },
      showcase: {
        alt: "Ovylon showcase",
        badge: "Showcase · Ovylon quality",
        caption: "Real examples of styles you can create in seconds.",
        dotAria: "View image {n}",
      },
      diff: {
        titleLine1: "Not another prompt app.",
        titleEmphasis: "Ovylon is built to produce and scale.",
        subtitle:
          "Create once and produce consistently: organize by campaigns, reuse styles, and ship creatives faster without losing direction or quality.",
      },
      pillars: {
        p1: {
          title: "Professional organization",
          desc: "Work like a brand: order, traceability, and repeatability in every delivery.",
          tag: "Pro",
        },
        p2: {
          title: "Business-ready quality",
          desc: "Sharper results, more detail, and better output for ads, ecommerce, or content.",
          tag: "HQ",
        },
        p3: {
          title: "Speed with control",
          desc: "Iterate fast with direction: keep consistency, create variations, and reuse configurations.",
          tag: "Control",
        },
      },
      exp: {
        titleLine1: "It’s not only generating images.",
        titleEmphasis: "It’s producing content with direction.",
        subtitle:
          "Create freely or work like a brand: repeat styles, organize campaigns, and scale without losing control.",
        creators: {
          pill: "For creators",
          title: "Create fast, iterate without friction",
          desc: "Ideal if you need speed and want to save what matters to repeat results.",
          b1: "2K / 4K / 8K resolutions for commercial use",
          b2: "Save your visual style",
          b3: "Projects + campaigns to organize production",
          b4: "Creation history (recover prompts and results)",
          b5: "Fast variations to find the best creative",
          b6: "Free generation in seconds",
          ctaPrimary: "Start creating →",
          ctaSecondary: "See how it works",
        },
        brands: {
          pill: "For brands & campaigns",
          title: "Scale like a professional team",
          desc: "Built to produce repeatable creatives, keep consistency, and work by projects and campaigns.",
          b1: "2K / 4K / 8K resolutions for commercial use",
          b2: "Persistent visual consistency for your brand",
          b3: "Repeatable creatives for campaigns (continuous production)",
          b4: "Projects + campaigns to organize production",
          b5: "Creation history (recover prompts and results)",
          b6: "Guided variations for continuous production",
          ctaPrimary: "Create campaigns →",
          ctaSecondary: "See how it works",
        },
      },
      compare: {
        titleLine1: "Built to produce content",
        titleEmphasis: "in series, with consistency and control.",
        subtitle:
          "It’s not just generating a pretty image: it’s repeating results, organizing campaigns, and shipping faster with quality.",
        table: { feature: "Feature", ovylon: "Ovylon", others: "Other tools" },
        other: { no: "No", sometimes: "Sometimes", depends: "Depends" },
        rows: {
          r1: "Projects + campaigns (organized work)",
          r2: "Repeatability and consistency for production",
          r3: "2K / 4K / 8K resolutions (commercial output)",
          r4: "Workflow designed for speed (without losing control)",
          r5: "Usable history (recover prompts and results)",
          r6: "Fast variations to pick the best creative",
          r7: "Persistent visual consistency for brands",
          r8: "Repeatable creatives for campaigns (continuous production)",
        },
        note: "*General comparison based on common market approaches.",
      },
      footer: {
        rights: "All rights reserved.",
        about: "What is Ovylon",
        support: "Support",
        privacy: "Privacy",
        terms: "Terms",
      },
    },

    plans: {
      title: "Simple, clear and scalable plans",
      subtitle:
        "Start free and scale only when you need it. Built for creators and brands who produce content seriously.",
      ctaSignup: "Create account →",
      ctaLogin: "Log in",
      recommended: "Recommended",
      freePrice: "Free",
      perMonth: "/mo",
      creditsPerMonth: "credits/month",
      planDesc: {
        free: "Try Ovylon with zero friction.",
        basic: "Create content with more control.",
        pro: "Produce seriously and scale.",
        premium: "Maximum performance and consistency.",
      },
      features: {
        resolutions: "Resolutions:",
        brandKit: "Brand Kit:",
        useBrandInGen: "Use brand in generation:",
        advancedFields: "Advanced fields:",
        activeKits: "Active brand kits:",
      },
      values: {
        persistent: "Persistent",
        demoNoSave: "Demo (no save)",
        yes: "Yes",
        no: "No",
        unlimited: "Unlimited",
      },
      btnStartFree: "Start free",
      btnChoosePlan: "Choose plan",
      haveAccountUpgrade: "I already have an account → Upgrade plan",
      disclaimer: "Informational pricing for now. Payments will be connected in the billing phase.",
    },

    login: {
      backToHome: "Back to home",
      subtitle: "Log in to continue your creation flow.",
      emailLabel: "Email",
      emailPlaceholder: "you@email.com",
      passwordLabel: "Password",
      passwordPlaceholder: "Your password",
      showPassword: "Show password",
      hidePassword: "Hide password",
      show: "Show",
      hide: "Hide",
      loading: "Loading...",
      submit: "Log in",
      createAccount: "Create new account",
      forgotPassword: "Forgot your password?",
      footerNote: "By logging in, you continue your flow in Ovylon.",
      errors: {
        emailNotConfirmedTitle: "Your email is not confirmed yet.",
        emailNotConfirmedHelp:
          "Check your inbox (and Spam/Promotions) and confirm your email. Then come back and log in.",
        invalidCredentials: "Incorrect email or password.",
        generic: "Could not log in. Please try again.",
      },
    },

    register: {
      backToHome: "Back to home",

      subtitleLine1: "Create your account and start your creation flow",
      subtitleLine2: "with free credits.",

      nameLabel: "Name",
      namePlaceholder: "Your name",

      emailLabel: "Email",
      emailPlaceholder: "you@email.com",

      passwordLabel: "Password",
      passwordPlaceholder: "Minimum 6 characters",

      confirmPasswordLabel: "Confirm password",
      confirmPasswordPlaceholder: "Repeat your password",

      showPassword: "Show password",
      hidePassword: "Hide password",
      show: "Show",
      hide: "Hide",

      creating: "Creating account...",
      submit: "Create account",

      alreadyHave: "Already have an account?",
      loginLink: "Log in",

      footerNote: "By creating your account, you can confirm your email and start with free credits.",

      confirm: {
        title: "Check your email ✉️",
        sentTo: "We sent a confirmation link to",
        step1: '1) Open the email and click "Confirm email".',
        step2: "2) Then come back here and log in.",
        tip: "Tip: check Spam / Promotions if you don’t see it in 1–2 minutes.",
        ctaConfirmed: "I already confirmed → Log in",
        ctaUseAnother: "I used another email",
        securityNote: "For security, your account won’t have active credits until you confirm your email and log in.",
      },

      errors: {
        nameRequired: "Please enter your name.",
        emailInvalid: "Please enter a valid email.",
        passwordMin: "Password must be at least 6 characters.",
        passwordMismatch: "Passwords do not match.",
        generic: "Could not create account. Try a different email.",
      },
    },
  },

  es: {
    common: {
      language: "Idioma",
      es: "ES",
      en: "EN",
    },
    shell: {
      brandStudio: "Brand Studio",
      creatorStudio: "Brand Studio",
      searchBrand: "Buscar prompts, estilos, kits...",
      searchCreator: "Buscar prompts, estilos, 4k, upscale...",
      exploreNoSession: "Explorar (sin sesión)",
      previewHint: "Crea una cuenta o inicia sesión para generar",
      loginRequired: "Inicia sesión para acceder",
      login: "Iniciar sesión",
      signup: "Crear cuenta",
      logout: "Cerrar sesión",
      collapseMenu: "Contraer menú",
      expandMenu: "Expandir menú",
      creditsLoading: "créditos: …",
      credits: "créditos",
      plan: "Plan",
    },
    nav: {
      home: "Inicio",
      generator: "Generador IA",
      projects: "Proyectos",
      brandKit: "Brand Kit",
      settings: "Configuración",
    },

    home: {
      nav: {
        creator: "Creator",
        brand: "Brand",
        howItWorks: "Cómo funciona",
        pricing: "Precios",
        login: "Iniciar sesión",
        signup: "Crear cuenta",
        mobileLogin: "Entrar",
        mobileSignup: "Crear",
        openCreatorTitle: "Abrir Creator (Preview)",
        openBrandTitle: "Abrir Brand (Preview)",
      },
      hero: {
        titleLine1: "Crea contenido profesional con IA.",
        titleEmphasis: "Diseñado para creadores y marcas.",
        subtitleLine1: "Ovylon no es solo generación de imágenes.",
        subtitleLine2: "Es un sistema de creación que convierte ideas en resultados listos para negocio.",
        ctaPrimary: "Crear cuenta gratis →",
        ctaSecondary: "Entrar",
        note: "Sin tarjeta · Créditos gratis para empezar",
      },
      showcase: {
        alt: "Ovylon showcase",
        badge: "Showcase · Calidad Ovylon",
        caption: "Ejemplos reales de estilos que puedes crear en segundos.",
        dotAria: "Ver imagen {n}",
      },
      diff: {
        titleLine1: "No es otra app de prompts.",
        titleEmphasis: "Ovylon está diseñado para producir y escalar.",
        subtitle:
          "Crea una vez y vuelve a producir con consistencia: organiza por campañas, reutiliza estilos y acelera la salida de creativos sin perder dirección ni calidad.",
      },
      pillars: {
        p1: {
          title: "Organización profesional",
          desc: "Trabaja como marca: orden, trazabilidad y repetibilidad en cada entrega.",
          tag: "Pro",
        },
        p2: {
          title: "Calidad lista para negocio",
          desc: "Más nitidez, más detalle y mejor salida para anuncios, ecommerce o contenido.",
          tag: "HQ",
        },
        p3: {
          title: "Velocidad con control",
          desc: "Itera rápido con dirección: mantén consistencia, crea variaciones y reutiliza configuraciones.",
          tag: "Control",
        },
      },
      exp: {
        titleLine1: "No es solo generar imágenes.",
        titleEmphasis: "Es producir contenido con dirección.",
        subtitle: "Crea libremente o trabaja como marca: repite estilos, organiza campañas y escala sin perder control.",
        creators: {
          pill: "Para creadores",
          title: "Crea rápido, itera sin fricción",
          desc: "Ideal si necesitas producir contenido con velocidad y guardar lo importante para repetir resultados.",
          b1: "Resoluciones 2K / 4K / 8K para uso comercial",
          b2: "Guardado de estilo visual",
          b3: "Proyectos + campañas para organizar producción",
          b4: "Historial de creaciones (recupera prompts y resultados)",
          b5: "Variaciones rápidas para encontrar el mejor creativo",
          b6: "Generación libre en segundos",
          ctaPrimary: "Empezar a crear →",
          ctaSecondary: "Ver cómo funciona",
        },
        brands: {
          pill: "Para marcas y campañas",
          title: "Escala como un equipo profesional",
          desc: "Diseñado para producir creativos repetibles, mantener consistencia y trabajar por proyectos y campañas.",
          b1: "Resoluciones 2K / 4K / 8K para uso comercial",
          b2: "Consistencia visual persistente para tu marca",
          b3: "Creativos repetibles para campañas (producción continua)",
          b4: "Proyectos + campañas para organizar producción",
          b5: "Historial de creaciones (recupera prompts y resultados)",
          b6: "Variaciones guiadas para producción continua",
          ctaPrimary: "Crear campañas →",
          ctaSecondary: "Ver cómo funciona",
        },
      },
      compare: {
        titleLine1: "Diseñado para producir contenido",
        titleEmphasis: "en serie, con consistencia y control.",
        subtitle:
          "No se trata solo de generar una imagen bonita: se trata de repetir resultados, organizar campañas y acelerar entregas con calidad.",
        table: { feature: "Característica", ovylon: "Ovylon", others: "Otras herramientas" },
        other: { no: "✕", sometimes: "A veces", depends: "Depende" },
        rows: {
          r1: "Proyectos + campañas (trabajo organizado)",
          r2: "Repetibilidad y consistencia para producción",
          r3: "Resoluciones 2K / 4K / 8K (salida comercial)",
          r4: "Flujo diseñado para velocidad (sin perder control)",
          r5: "Historial usable (recupera prompts y resultados)",
          r6: "Variaciones rápidas para elegir el mejor creativo",
          r7: "Consistencia visual persistente para marca",
          r8: "Creativos repetibles para campañas (producción continua)",
        },
        note: "*Comparación general basada en enfoques comunes del mercado.",
      },
      footer: {
        rights: "Todos los derechos reservados.",
        about: "Qué es Ovylon",
        support: "Soporte",
        privacy: "Privacidad",
        terms: "Términos",
      },
    },

    plans: {
      title: "Planes simples, claros y escalables",
      subtitle:
        "Empieza gratis y escala solo cuando lo necesites. Diseñado para creadores y marcas que producen contenido en serio.",
      ctaSignup: "Crear cuenta →",
      ctaLogin: "Iniciar sesión",
      recommended: "Recomendado",
      freePrice: "Gratis",
      perMonth: "/mes",
      creditsPerMonth: "créditos/mes",
      planDesc: {
        free: "Para probar Ovylon sin fricción.",
        basic: "Para crear contenido con más control.",
        pro: "Para producir en serio y escalar.",
        premium: "Máximo rendimiento y consistencia.",
      },
      features: {
        resolutions: "Resoluciones:",
        brandKit: "Brand Kit:",
        useBrandInGen: "Usar marca en generación:",
        advancedFields: "Campos avanzados:",
        activeKits: "Brand Kits activas:",
      },
      values: {
        persistent: "Persistente",
        demoNoSave: "Demo (no guarda)",
        yes: "Sí",
        no: "No",
        unlimited: "Ilimitadas",
      },
      btnStartFree: "Empezar gratis",
      btnChoosePlan: "Elegir plan",
      haveAccountUpgrade: "Ya tengo cuenta → Mejorar plan",
      disclaimer: "Precios informativos por ahora. La activación de pagos se conecta en la fase de billing.",
    },

    login: {
      backToHome: "Volver al inicio",
      subtitle: "Accede para continuar tu flujo de creación.",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "tu@email.com",
      passwordLabel: "Contraseña",
      passwordPlaceholder: "Tu contraseña",
      showPassword: "Mostrar contraseña",
      hidePassword: "Ocultar contraseña",
      show: "Mostrar",
      hide: "Ocultar",
      loading: "Cargando...",
      submit: "Iniciar sesión",
      createAccount: "Crear cuenta nueva",
      forgotPassword: "¿Olvidaste tu contraseña?",
      footerNote: "Al iniciar sesión, continúas tu flujo en Ovylon.",
      errors: {
        emailNotConfirmedTitle: "Tu correo aún no está confirmado.",
        emailNotConfirmedHelp:
          "Revisa tu bandeja de entrada (y Spam/Promociones) y confirma el email. Luego vuelve e inicia sesión.",
        invalidCredentials: "Correo o contraseña incorrectos.",
        generic: "No se pudo iniciar sesión. Intenta de nuevo.",
      },
    },

    register: {
      backToHome: "Volver al inicio",

      subtitleLine1: "Crea tu cuenta y comienza tu flujo de creación",
      subtitleLine2: "con créditos gratis.",

      nameLabel: "Nombre",
      namePlaceholder: "Tu nombre",

      emailLabel: "Correo electrónico",
      emailPlaceholder: "tu@email.com",

      passwordLabel: "Contraseña",
      passwordPlaceholder: "Mínimo 6 caracteres",

      confirmPasswordLabel: "Confirmar contraseña",
      confirmPasswordPlaceholder: "Repite tu contraseña",

      showPassword: "Mostrar contraseña",
      hidePassword: "Ocultar contraseña",
      show: "Mostrar",
      hide: "Ocultar",

      creating: "Creando cuenta...",
      submit: "Crear cuenta",

      alreadyHave: "¿Ya tienes una cuenta?",
      loginLink: "Iniciar sesión",

      footerNote: "Al crear tu cuenta, podrás confirmar tu email y empezar con créditos gratis.",

      confirm: {
        title: "Revisa tu correo ✉️",
        sentTo: "Te enviamos un enlace de confirmación a",
        step1: '1) Abre el correo y haz clic en “Confirm email”.',
        step2: "2) Luego vuelve aquí e inicia sesión.",
        tip: "Tip: revisa Spam / Promociones si no lo ves en 1–2 minutos.",
        ctaConfirmed: "Ya confirmé → Iniciar sesión",
        ctaUseAnother: "Usé otro correo",
        securityNote: "Por seguridad, tu cuenta no tendrá créditos activos hasta confirmar el email e iniciar sesión.",
      },

      errors: {
        nameRequired: "Ingresa tu nombre.",
        emailInvalid: "Ingresa un correo válido.",
        passwordMin: "La contraseña debe tener al menos 6 caracteres.",
        passwordMismatch: "Las contraseñas no coinciden.",
        generic: "No se pudo crear la cuenta. Intenta con otro correo.",
      },
    },
  },
};
